// @spec DHB-STR-014
// @spec DHB-STR-009
// Combined data entry page — file upload and manual (bulk) entry in one view

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FileUp, Loader2, Table } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDeleteUpload, useUpload } from '@/features/datahub/api/use-uploads';
import { BulkEntryPage } from '@/features/datahub/entry';
import type { UploadedDocument } from '@/features/datahub/types';
import { DataFileUpload, FilePreview } from '@/features/datahub/uploads';

export const Route = createFileRoute('/_authenticated/datahub/upload')({
  component: DataEntryPage,
  validateSearch: (search: Record<string, unknown>) => ({
    uploadId: typeof search.uploadId === 'string' ? search.uploadId : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
});

function DataEntryPage() {
  const { t } = useTranslation('datahub');
  const navigate = useNavigate();
  const { uploadId, tab } = Route.useSearch();

  const activeTab = tab === 'manual' ? 'manual' : 'upload';

  const [uploadedFile, setUploadedFile] = useState<UploadedDocument | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { deleteUpload, isDeleting } = useDeleteUpload();

  const {
    upload: fetchedUpload,
    isLoading: isFetchingUpload,
    isError: isFetchError,
    error: fetchError,
  } = useUpload({
    uploadId: uploadId ?? '',
    enabled: !!uploadId && !uploadedFile,
  });

  useEffect(() => {
    if (fetchedUpload && !uploadedFile) {
      setUploadedFile(fetchedUpload);
    }
  }, [fetchedUpload, uploadedFile]);

  useEffect(() => {
    if (isFetchError && fetchError) {
      setError(fetchError.message);
    }
  }, [isFetchError, fetchError]);

  const handleUploadComplete = (upload: UploadedDocument) => {
    setUploadedFile(upload);
    setError(null);
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleContinue = () => {
    if (uploadedFile) {
      void navigate({
        to: '/datahub/uploads/$uploadId/map',
        params: { uploadId: uploadedFile.id },
      });
    }
  };

  const handleCancel = async () => {
    if (uploadedFile) {
      try {
        await deleteUpload(uploadedFile.id);
        setUploadedFile(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('uploads.failedToDelete'));
      }
    }
  };

  const handleTabChange = (value: string) => {
    void navigate({
      to: '/datahub/upload',
      search: { tab: value === 'manual' ? 'manual' : undefined },
      replace: true,
    });
  };

  const isLoadingFromParam = !!uploadId && isFetchingUpload && !uploadedFile;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">{t('uploads.dataEntryTitle', 'Data Entry')}</h1>
        <p className="mt-1 text-muted-foreground">
          {t(
            'uploads.dataEntryDescription',
            'Upload files or enter data manually to populate your data hub.'
          )}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="upload" className="gap-2">
            <FileUp className="h-4 w-4" />
            {t('common:nav.uploadData', 'Upload Data')}
          </TabsTrigger>
          <TabsTrigger value="manual" className="gap-2">
            <Table className="h-4 w-4" />
            {t('common:nav.manualEntry', 'Manual Entry')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-6">
          <div className="mx-auto max-w-4xl">
            {error && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {isLoadingFromParam ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : uploadedFile ? (
              <FilePreview
                upload={uploadedFile}
                onContinue={handleContinue}
                onCancel={() => void handleCancel()}
              />
            ) : (
              <DataFileUpload
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
                disabled={isDeleting}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="manual" className="mt-6">
          <div className="-mx-8 -mb-8">
            <BulkEntryPage />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
